import { type ClassValue } from 'clsx';
import { isStringLiteral } from '@enonic/js-utils/value/isStringLiteral';
import { ErrorMessage } from '@hdir/ui/error-message';
import { type IconDefinition } from '@fortawesome/pro-light-svg-icons';
import { type FontAwesomeIconProps } from '@fortawesome/react-fontawesome';
import React, {
	useEffect,
	useId,
	useRef,
} from 'react';
import {
	type FieldError,
	type FieldPath,
	type FieldPathValue,
	type FieldValues,
	type UseFormSetValue,
	useController,
	useWatch,
} from 'react-hook-form';

import {
	type ElementProps,
	Span,
} from '/react4xp/components/Element';
import { InlineRow, Stack } from '/react4xp/components/Flex';
import { Fieldset } from '/react4xp/components/react-hook-form/Fieldset';
import { useFormContext } from '/react4xp/components/react-hook-form/FormContext';
import { isShowIfFunction, type ShowIfFunction } from '/react4xp/components/react-hook-form/isShowIfFunction';

import { ButtonCheckbox } from './ButtonCheckbox';

export interface ButtonCheckboxGroupOption {
	icon?: IconDefinition;
	iconColor?: FontAwesomeIconProps['color'];
	label?: string;
	value: string;
}

type FieldsetAttributes = React.FieldsetHTMLAttributes<HTMLFieldSetElement>;

export type ButtonCheckboxGroupProps<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Omit<FieldsetAttributes, 'className'|'color'|'name'|'translate'> & {
	afterClick?: (params: {
		newValue: string | string[] | null | undefined;
		setValue: UseFormSetValue<TFieldValues>;
	}) => void
	error?: string;
	errorSummaryLabel?: string;
	description?: string;
	gap?: ElementProps['gap'];
	multiple?: boolean;
	name: TName;
	options: ButtonCheckboxGroupOption[];
	label?: string;
	className?: ClassValue;
	showIf?: ShowIfFunction<TFieldValues>;
};

export function ButtonCheckboxGroup<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
	afterClick,
	description,
	disabled: disabledProp,
	error: errorProp,
	gap = 'sm',
	name,
	options,
	label,
	errorSummaryLabel = isStringLiteral(label) ? label : undefined,
	showIf,
	...otherProps
}: ButtonCheckboxGroupProps<TFieldValues, TName>): JSX.Element | null {
	const id = useId(); // Generate unique IDs for accessibility
	const descriptionId = useId();

	const {
		control,
		getRequired,
		setValue,
		setErrorSummaryLabel,
		setFocusRef,
		setScrollRef,
	} = useFormContext<TFieldValues>();

	const isRequired: boolean = getRequired
		? getRequired(name)
		: false;

	const {
		field: {
			disabled,
			onChange: useControllerOnChange,
			// ref: useControllerFieldRef, // I keep my own REAL refs for ErrorSummary.
			value: groupValue,
		},
		fieldState: {
			error: errorState,
		},
	} = useController<TFieldValues, TName>({
		control,
		disabled: disabledProp,
		name,
	});

	const error = errorState || errorProp;

	const inputRefs = useRef<HTMLInputElement[]>([]);
	const focusRef = useRef<HTMLInputElement>();
	const scrollRef = useRef<HTMLLegendElement>();
	const typedGroupValue = groupValue || [] as string[];

	// Make the array the same size as options, so refs can be added via index.
	useEffect(() => {
		inputRefs.current = inputRefs.current.slice(0, options.length);
	}, [options.length]);

	useEffect(() => {
		if (errorSummaryLabel) {
			setErrorSummaryLabel({field: name, label: errorSummaryLabel});
		}
	},[errorSummaryLabel]);

	useEffect(() => {
		if (focusRef.current) {
			setFocusRef({ field: name, el: focusRef.current });
		}
	},[focusRef.current]);

	useEffect(() => {
		if (scrollRef.current) {
			setScrollRef({ field: name, el: scrollRef.current });
		}
	},[scrollRef.current]);

	if (isShowIfFunction(showIf) && !showIf({
		// eslint-disable-next-line @typescript-eslint/no-unsafe-return
		watch: (watchParams) => useWatch({...watchParams, control}) as FieldPathValue<TFieldValues, FieldPath<TFieldValues>>,
	})) {
		return null;
	}

	return (
		<Fieldset
			{...otherProps}
			borderless
			className='text-md'
			disabled={disabled}
			gap={gap}
			legend={label}
			legendRef={scrollRef}
			name={name}
			required={isRequired}
		>
			<Span
				className={[
					'text-sm',
					{ 'text-neutral-disabled': disabled },
				]}
				id={descriptionId}
			>{description}</Span>
			<Stack
				aria-describedby={descriptionId}
				className='items-start'
				role='group'
			>
				<InlineRow
					boxShadow={error ? 'after:insetRingMdError' : undefined}
					className={[
						'after:content-[""] after:absolute after:h-full after:pointer-events-none after:w-full after:z-10',
						'flex-wrap relative',
					]}
					gap={gap}
				>
					{
						options.map(({
							icon,
							iconColor,
							label,
							value,
						}, index) => {
							const checked = typedGroupValue.includes(value);
							function onChange(): void {
								const newValue = checked
									? (typedGroupValue as string[]).filter(item => item !== value)
									: typedGroupValue.concat(value);
								useControllerOnChange(newValue);
								if (afterClick) afterClick({
									newValue,
									setValue,
								});
							};
							return (
								<ButtonCheckbox
									checked={checked}
									disabled={disabled}
									error={!!error}
									icon={icon}
									iconColor={iconColor}
									id={`${id}-${index}`}
									name={name}
									label={label}
									onChange={onChange}
									onKeyDown={(event) => {
										if (
											event.code === 'ArrowDown'
											|| event.code === 'ArrowRight'
										) {
											event.preventDefault();
											const newIndex = index === (options.length - 1) ? 0 : index + 1;
											inputRefs.current[newIndex].focus({
												preventScroll: true,
											});
										}
										if (
											event.code === 'ArrowLeft'
											|| event.code === 'ArrowUp'
										) {
											event.preventDefault();
											const newIndex = index === 0 ? (options.length - 1) : index - 1;
											inputRefs.current[newIndex].focus({
												preventScroll: true,
											});
										}
										if (
											event.code === 'Home'
											|| event.code === 'PageUp'
										) {
											event.preventDefault();
											inputRefs.current[0].focus({
												preventScroll: true,
											});
										}
										if (
											event.code === 'End'
											|| event.code === 'PageDown'
										) {
											event.preventDefault();
											inputRefs.current[options.length - 1].focus({
												preventScroll: true,
											});
										}
									}}
									onKeyUp={(event) => {
										if (
											event.code === 'Enter'
										) {
											onChange();
										}
									}}
									ref={(inputElement) => {
										if (inputElement) {
											inputRefs.current[index] = inputElement;
											if (index === 0) {
												focusRef.current = inputElement;
											}
										}
									}}
								/>
							);
						})
					}
				</InlineRow>
			</Stack>
			{error ? <ErrorMessage>
				{isStringLiteral(error) ? error : (error as FieldError).message}
			</ErrorMessage>: null}
		</Fieldset>
	);
}
